/**
 * Pressed codes to input axes and buttons through the account's bindings (Phase 8; BUILD_PLAN
 * section 7 defaults live in the shared `DEFAULT_BINDINGS`). A binding is a `KeyboardEvent.code`
 * or a mouse button (`Mouse0`..`Mouse4`); the controller keeps both kinds in one pressed set.
 */
import { ACTIONS, type Action, type Bindings } from '@steelwake/shared/progression';

export interface KeyAxes {
  moveX: number;
  moveY: number;
  boost: boolean;
  jump: boolean;
  ability: boolean;
  ultimate: boolean;
  vent: boolean;
  melee: boolean;
}

export const RELEASED_KEYS: Readonly<KeyAxes> = Object.freeze({
  moveX: 0,
  moveY: 0,
  boost: false,
  jump: false,
  ability: false,
  ultimate: false,
  vent: false,
  melee: false,
});

/** Every keyboard code the bindings use: the controller swallows these while a match runs. */
export function boundKeys(bindings: Bindings): Set<string> {
  const keys = new Set<string>();
  for (const action of ACTIONS) {
    for (const b of bindings[action]) if (!b.startsWith('Mouse')) keys.add(b);
  }
  return keys;
}

export function isDown(pressed: ReadonlySet<string>, bindings: Bindings, action: Action): boolean {
  return bindings[action].some((b) => pressed.has(b));
}

export function mapKeys(pressed: ReadonlySet<string>, bindings: Bindings): KeyAxes {
  const forward = isDown(pressed, bindings, 'moveForward') ? 1 : 0;
  const back = isDown(pressed, bindings, 'moveBack') ? 1 : 0;
  const right = isDown(pressed, bindings, 'strafeRight') ? 1 : 0;
  const left = isDown(pressed, bindings, 'strafeLeft') ? 1 : 0;
  return {
    moveX: right - left,
    moveY: forward - back,
    boost: isDown(pressed, bindings, 'boost'),
    jump: isDown(pressed, bindings, 'jump'),
    ability: isDown(pressed, bindings, 'ability'),
    ultimate: isDown(pressed, bindings, 'ultimate'),
    vent: isDown(pressed, bindings, 'vent'),
    melee: isDown(pressed, bindings, 'melee'),
  };
}

/** The code a mouse button reports as a binding. */
export function mouseCode(button: number): string {
  return `Mouse${String(button)}`;
}
